import React, {
  JSX,
  JSXElementConstructor,
  KeyboardEvent,
  MouseEvent,
  ReactElement,
  Ref,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import ReactDatePicker from 'react-datepicker';
import { ConfigModel } from '@ibe/services';
import { CustomDateDisplay } from './CustomDateDisplay';
import { DateWithKeyboardSupport } from './DateWithKeyboardSupport';
import { CustomsProps, DatePickerProps } from './DatePickerInModal';
import { Day } from 'date-fns';
import DataTestIdProp from '../DataTestIdProp';
import { useAppService, useConfig } from '@ibe/components';
import { CustomDisplayProps, OnChangeDate, OnChangeDateIntersection } from './CustomDisplayProps';
import MegConfigModel from '../../../Config/MegConfigModel';

export type ReactDatePickerRendererProps = {
  reactDatePickerRef?: Ref<ReactDatePicker>;
  showInModal?: boolean;
  toggleDropdown?: () => void;
} & DatePickerProps &
  DataTestIdProp &
  CustomsProps &
  CustomDisplayProps;

const getCalendarStartDay = (configuration: ConfigModel, lang: string): number => {
  const weekStartsOn = configuration.calendar?.weekStartsOn || {};
  return weekStartsOn[lang] ?? weekStartsOn[lang.substring(2)] ?? weekStartsOn?.en ?? 1;
};

const ReactDatePickerRenderer = (props: ReactDatePickerRendererProps): JSX.Element => {
  const {
    reactDatePickerRef,
    dataTestId,
    locale,
    id,
    customCalendarContainer,
    renderCustomHeader,
    onChange,
    onCalendarClose,
    onCalendarOpen,
    customInput,
    placeholderText,
    showInModal,
    withKeyboardSupport,
    toggleDropdown,
    date,
    displayFormat,
    theme,
    className,
    onInputChange,
    minDate,
    maxDate,
    inputClassName,
    useFormValidation,
    setValueToNullWhenFormatIsIncorrect,
    focusSelectedMonth,
    inline,
    noDatePickerOverflow,
    onMonthChange,
    withHeader,
    renderDayContents,
    noInitialValueInput,
    showPopperArrow
  } = props;

  useEffect(() => {
    const fixKeyboardNavigation = () => {
      const baseSelector =
        '.react-datepicker__day:not(.react-datepicker__day--disabled):not(.react-datepicker__day--outside-month)';
      const selectedClass = 'react-datepicker__day--keyboard-selected';

      if (!document.querySelector(`${baseSelector}[tabindex="0"]`)) {
        const day = document.querySelector(`.react-datepicker__month ${baseSelector}`);
        if (day && day.getAttribute('tabindex') === '-1') {
          day.setAttribute('tabindex', '0');
          day.addEventListener('focus', () => day.classList.add(selectedClass));
          day.addEventListener('blur', () => day.classList.remove(selectedClass));
        }
      }

      enableMonthPickerKeyboardSupport();
      enableMonthDropdownKeyboardSupport();
    };

    fixKeyboardNavigation();

    const observer = new MutationObserver(fixKeyboardNavigation);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, []);

  const enableMonthPickerKeyboardSupport = () => {
    const monthNode = document.querySelector<HTMLElement>(
      '.react-datepicker__month-year-read-view'
    );

    if (monthNode && monthNode.getAttribute('tabindex') !== '0') {
      monthNode.setAttribute('tabindex', '0');

      monthNode.addEventListener('keydown', event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          monthNode.click();
        }
      });

      monthNode.focus();
    }
  };
  const enableMonthDropdownKeyboardSupport = () => {
    const options = document.querySelectorAll<HTMLElement>(
      '.react-datepicker__month-year-option, .react-datepicker__month-year-option--selected_month-year'
    );

    if (options.length > 0 && !options[0].hasAttribute('tabindex')) {
      options.forEach((option, index) => {
        option.setAttribute('tabindex', '0');

        option.addEventListener('keydown', event => {
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            const next = options[index + 1];
            if (next) next.focus();
          } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            const prev = options[index - 1];
            if (prev) prev.focus();
          } else if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            option.click();
          }
        });
      });

      const active = document.querySelector<HTMLElement>(
        '.react-datepicker__month-year-option--selected_month-year'
      );
      if (active) {
        active.focus();
      } else if (options[0]) {
        options[0].focus();
      }
    }
  };

  const config = useConfig() as MegConfigModel;

  const appService = useAppService();

  const [inputMethod, setInputMethod] = useState<'mouse' | 'keyboard' | null>(null);

  const handleMouseDown = useCallback(() => setInputMethod('mouse'), []);
  const handleKeyDown = useCallback(() => setInputMethod('keyboard'), []);

  useEffect(() => {
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleMouseDown, handleKeyDown]);

  const dayClassName = useCallback(
    (_: Date) => (inputMethod == 'keyboard' ? 'keyboard-controlled' : 'mouse-controlled'),
    [inputMethod]
  );

  const popperModifiers = useMemo(
    () => [
      {
        name: 'flip',
        options: {
          flipVariations: false,
          allowedAutoPlacements: ['bottom']
        },
        fn: (): Promise<{}> => {
          return Promise.resolve({});
        }
      },
      ...(noDatePickerOverflow
        ? [
            {
              name: 'preventOverflow',
              options: {
                rootBoundary: 'viewport'
              },
              fn: (): Promise<{}> => {
                return Promise.resolve({});
              }
            }
          ]
        : [])
    ],
    [noDatePickerOverflow]
  );

  const handleChange = useCallback(
    (dates: OnChangeDate, event?: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>): void => {
      if (showInModal) {
        if (Array.isArray(dates)) {
          if (dates.every(Boolean)) {
            if (toggleDropdown) {
              toggleDropdown();
            }
          }
        }
      }
      if (onChange) {
        onChange(dates as OnChangeDateIntersection, event);
      }
    },
    [onChange, showInModal, toggleDropdown]
  );

  const today = new Date();
  const calculatedMaxDate = new Date();
  const maxMonths = config.monthPickerMaxMonths ?? 11;
  calculatedMaxDate.setMonth(today.getMonth() + maxMonths);

  return (
    <ReactDatePicker
      {...props}
      selectsRange={props.selectsRange as undefined}
      ref={reactDatePickerRef}
      data-testid={dataTestId}
      locale={locale}
      calendarContainer={customCalendarContainer}
      renderCustomHeader={
        withHeader
          ? customHeaderProps => renderCustomHeader({ ...customHeaderProps, locale })
          : undefined
      }
      renderDayContents={renderDayContents}
      showMonthYearDropdown
      onChange={handleChange}
      onCalendarOpen={onCalendarOpen}
      onCalendarClose={onCalendarClose}
      dayClassName={dayClassName}
      customInput={
        (customInput ||
          (withKeyboardSupport && (
            <DateWithKeyboardSupport
              date={date}
              format={displayFormat}
              theme={theme}
              id={id}
              dataTestId={dataTestId}
              placeholder={placeholderText || ''}
              className={className}
              onInputChange={onInputChange}
              minDate={minDate}
              maxDate={maxDate}
              inputClassName={inputClassName}
              useFormValidation={useFormValidation}
              setValueToNullWhenFormatIsIncorrect={setValueToNullWhenFormatIsIncorrect}
              noInitialValueInput={noInitialValueInput}
            />
          )) ||
          ((date || placeholderText) && (
            <CustomDateDisplay
              date={date}
              format={displayFormat}
              theme={theme}
              dataTestId={dataTestId}
              placeholder={placeholderText || ''}
              className={className}
            />
          ))) as ReactElement<unknown, string | JSXElementConstructor<unknown>>
      }
      placeholderText={placeholderText}
      selected={date}
      popperPlacement="bottom-start"
      focusSelectedMonth={focusSelectedMonth}
      popperClassName="iso__date-picker__popper"
      popperModifiers={popperModifiers}
      minDate={today}
      maxDate={calculatedMaxDate}
      calendarStartDay={
        getCalendarStartDay(config, appService.lang || config.defaultLanguage) as Day | undefined
      }
      onMonthChange={onMonthChange}
      inline={inline}
      showPopperArrow={showPopperArrow}
    />
  );
};

export default ReactDatePickerRenderer;
